import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, ArrowLeftRight, MousePointerClick } from 'lucide-react';
import { Skeleton, IconButton } from '@vnetwork/ui/primitives';
import {
  ScheduleCalendar,
  StatusPill,
  type ScheduleRow,
  type ScheduleMember,
} from '@vnetwork/ui/data';
import { useOncallAssignments, type OncallAssignment } from '@/api/dashboard-scheduling';
import { WidgetCard } from './WidgetCard';
import {
  mondayOf,
  toDateKey,
  weekDays,
  weekRangeLabel,
  buildMemberColors,
} from './schedule-week';

const cellKey = (scheduleId: number, dayKey: string) => `${scheduleId}|${dayKey}`;

/** Trim "09:00:00" / ISO datetime → "09:00". */
function fmtTime(t?: string): string {
  if (!t) return '';
  const s = t.includes('T') ? t.split('T')[1] : t;
  return s.slice(0, 5);
}

interface SelectedCell {
  scheduleId: number;
  scheduleName: string;
  dayLabel: string;
  dayKey: string;
}

/**
 * On-call roster — REAL /oncall/assignments for the visible week, as a
 * `ScheduleCalendar` (schedule × day → employees on-call). Clicking a day cell
 * opens an inline detail panel below: each shift's hours, status and, when the
 * shift was reassigned, who it was swapped from and why. Mirrors the legacy
 * on-call timeline's scheduled/swap detail — in-place, no navigation.
 */
export function OncallScheduleWidget({ department: _department }: { department?: string } = {}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const monday = useMemo(() => mondayOf(new Date(), weekOffset), [weekOffset]);
  const days = useMemo(() => weekDays(monday), [monday]);

  const startDate = days[0].key;
  const endDate = days[days.length - 1].key;
  const { data: assignments = [], isLoading } = useOncallAssignments(startDate, endDate);

  const memberColors = useMemo(
    () => buildMemberColors(assignments.map((a) => a.employee_id)),
    [assignments],
  );

  // Full assignment records per schedule/day, for the detail panel.
  const detailByCell = useMemo(() => {
    const m = new Map<string, OncallAssignment[]>();
    for (const a of assignments) {
      const dayKey = a.oncall_date?.slice(0, 10) || toDateKey(new Date(a.oncall_date));
      const key = cellKey(a.oncall_schedule_id, dayKey);
      (m.get(key) ?? m.set(key, []).get(key)!).push(a);
    }
    return m;
  }, [assignments]);

  // Group assignments into rows (one per on-call schedule) × day cells.
  const rows = useMemo<ScheduleRow[]>(() => {
    const bySchedule = new Map<number, { name: string; cells: Record<string, ScheduleMember[]> }>();
    for (const a of assignments) {
      let group = bySchedule.get(a.oncall_schedule_id);
      if (!group) {
        group = { name: a.oncall_schedule_name || 'Untitled schedule', cells: {} };
        bySchedule.set(a.oncall_schedule_id, group);
      }
      const dayKey = a.oncall_date?.slice(0, 10) || toDateKey(new Date(a.oncall_date));
      const cell = (group.cells[dayKey] ??= []);
      if (!cell.some((m) => m.id === a.employee_id)) {
        cell.push({ id: a.employee_id, name: a.employee_name, color: memberColors.get(a.employee_id) });
      }
    }
    return Array.from(bySchedule.entries()).map(([id, group]) => ({
      id,
      label: group.name,
      cells: group.cells,
    }));
  }, [assignments, memberColors]);

  const legend = useMemo(() => {
    const seen = new Map<number, ScheduleMember>();
    for (const a of assignments) {
      if (!seen.has(a.employee_id)) {
        seen.set(a.employee_id, { id: a.employee_id, name: a.employee_name, color: memberColors.get(a.employee_id) });
      }
    }
    return Array.from(seen.values());
  }, [assignments, memberColors]);

  const selectedShifts = selected
    ? detailByCell.get(cellKey(selected.scheduleId, selected.dayKey)) ?? []
    : [];

  return (
    <WidgetCard
      title="On-call Timeline"
      description={weekRangeLabel(monday)}
      actions={
        <div style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
          <IconButton size="sm" variant="ghost" aria-label="Previous week" onClick={() => { setWeekOffset((w) => w - 1); setSelected(null); }}>
            <ChevronLeft size={16} />
          </IconButton>
          <IconButton size="sm" variant="ghost" aria-label="Next week" onClick={() => { setWeekOffset((w) => w + 1); setSelected(null); }}>
            <ChevronRight size={16} />
          </IconButton>
        </div>
      }
    >
      {isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 34 }} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--vn-space-3)' }}>
          <ScheduleCalendar
            rows={rows}
            days={days}
            rowHeader="Schedule"
            emptyText="No on-call assignments this week"
            onCellClick={({ row, day }) =>
              setSelected({
                scheduleId: Number(row.id),
                scheduleName: typeof row.label === 'string' ? row.label : String(row.label),
                dayLabel: `${day.label} ${day.sublabel ?? ''}`.trim(),
                dayKey: day.key,
              })
            }
          />

          {selected ? <ShiftDetail selected={selected} shifts={selectedShifts} memberColors={memberColors} /> : (
            <div style={detailHintStyle}>
              <MousePointerClick size={14} /> Click a day to see shift details
            </div>
          )}

          {legend.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', paddingTop: 'var(--vn-space-1)' }}>
              {legend.map((m) => (
                <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color ?? 'var(--vn-primary-solid)' }} />
                  {m.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </WidgetCard>
  );
}

const detailHintStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--vn-space-1)',
  fontSize: 'var(--vn-font-size-xs)',
  color: 'var(--vn-fg-subtle)',
  padding: 'var(--vn-space-2) var(--vn-space-1)',
};

/** Inline detail for the clicked schedule/day — one row per shift. */
function ShiftDetail({
  selected,
  shifts,
  memberColors,
}: {
  selected: SelectedCell;
  shifts: OncallAssignment[];
  memberColors: Map<number, string | undefined>;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--vn-border-subtle)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-muted)',
        padding: 'var(--vn-space-3)',
        display: 'grid',
        gap: 'var(--vn-space-2)',
      }}
    >
      <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
        {selected.scheduleName} · {selected.dayLabel}
      </div>
      {shifts.length === 0 ? (
        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>No one on-call this day.</div>
      ) : (
        shifts.map((a) => {
          const swapped =
            a.original_employee_id != null && a.original_employee_id !== a.employee_id && a.original_employee_name;
          const time = [fmtTime(a.oncall_start_time), fmtTime(a.oncall_end_time)].filter(Boolean).join(' – ');
          return (
            <div key={a.oncall_assignment_id} style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: memberColors.get(a.employee_id) ?? 'var(--vn-primary-solid)' }} />
                  {a.employee_name}
                </span>
                {time ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    <Clock size={12} /> {time}
                  </span>
                ) : null}
                {a.oncall_assignment_status ? (
                  <StatusPill tone={a.oncall_assignment_status === 'active' ? 'success' : 'muted'}>
                    {a.oncall_assignment_status}
                  </StatusPill>
                ) : null}
              </div>
              {swapped ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-warning-text)' }}>
                  <ArrowLeftRight size={12} /> Swapped from {a.original_employee_name}
                  {a.reassignment_reason ? ` · ${a.reassignment_reason}` : ''}
                </div>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}
